/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';
import { useQuery } from '@apollo/client';

import { CompanyUserReviewStatus, IdDocumentCase } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import { useConsumeQueryParam } from 'common/bento/lib/useConsumeQueryParam';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';
import IdentitySuccess from 'features/Onboarding/pages/IdentitySuccess';
import logger from 'helpers/logger/logger';

import { IdentityModuleDataDocument } from './graphql/queries/identityModuleData.gql';
import getIdentityErrors from './libs/getIdentityErrors';
import getIdentityStatus from './libs/getIdentityStatus';
import getInitialFormValues from './libs/getInitialFormValues';
import identitySchema from './libs/identitySchema';
import machineConfig, { model, State } from './machine';
import BiometricConsent from './pages/BiometricConsent';
import FourthlineIdCheck from './pages/FourthlineIdCheck/FourthlineIdCheck';
import Guide from './pages/Guide';
import IdentityReviewError from './pages/IdentityReviewError';
import NativeIdCheckError from './pages/NativeIdCheckError/NativeIdCheckError';
import OnfidoIdCheck from './pages/OnfidoIdCheck';
import PersonalInformation from './pages/PersonalInformation';
import Recap from './pages/Recap/Recap';
import ResidencyDocument from './pages/ResidencyDocument/ResidencyDocument';

interface IdentityProps {
  companyProfileId: string;
  companyUserId: string;
}

/**
 * This module is responsible to verify the identity of the user.
 *
 * @input A user whose identity data is not registered, and whose identity is not certified.
 * @output A user whose identity data is registered and with an Onfido-certified ID check.
 */
const Identity = asBentoModule<IdentityProps>(
  ({ companyProfileId, onDone }) => {
    const query = useQuery(IdentityModuleDataDocument, {
      variables: { companyProfileId },
    });

    const bootstrap = useConsumeQueryParam(`bootstrap:${machineConfig.id}`);

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(queryResult) => (
          <IdentityComponent
            moduleData={{ ...queryResult, bootstrap }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

const IdentityComponent = withStateMachine<typeof model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;
    const { companyUser, companyUsers } = moduleData.viewer;

    const stringifiedState = stringifyState<State>(state);

    const onPrev = useCallback(() => send(model.events.PREV()), [send]);

    const handleFourthlineRestartIdCheck = useCallback(() => {
      send(model.events.RESTART_ID_CHECK());
    }, [send]);

    const handleFourthlineVerificationCompleted = useCallback(() => {
      send(model.events.FOURTHLINE_SUCCESS());
    }, [send]);

    const identityStatus = getIdentityStatus({ companyUser });

    switch (stringifiedState) {
      case State.IDENTITY_REVIEW_ERROR: {
        const errors = getIdentityErrors({
          kycDocuments: moduleData.viewer.companyUser.kycReviewDocuments,
          kycReviewError:
            moduleData.viewer.companyUser.lastKycReview?.error ?? null,
          providerReviewError: moduleData.viewer.companyUser.kycIdProviderError,
          stepStatus: identityStatus,
        });

        return (
          <IdentityReviewError
            errors={errors}
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.IDENTITY_FORM:
      case State.IDENTITY_SEND:
      case State.ONFIDO_NATIVE_IDENTITY_CHECK:
        return (
          <PersonalInformation
            initialValues={getInitialFormValues(moduleData.viewer.profile)}
            isSubmitting={
              state.matches(State.IDENTITY_SEND) ||
              state.matches(State.ONFIDO_NATIVE_IDENTITY_CHECK)
            }
            onPrev={onPrev}
            onSubmit={(values) => send(model.events.SUBMIT(values))}
          />
        );

      case State.RESIDENCY_DOCUMENT:
        return (
          <ResidencyDocument
            onContinue={(values) => {
              if (values.ownedDocumentType === 'residencyCard') {
                return send(
                  model.events.SUBMIT_HAS_RESIDENCY_CARD({
                    isExpired: !values.isResidencyCardValid,
                  }),
                );
              }

              if (values.ownedDocumentType === 'visa') {
                return send(model.events.SUBMIT_HAS_VISA());
              }

              return send(model.events.SUBMIT_HAS_NO_RESIDENCY_DOCUMENT());
            }}
            onPrev={onPrev}
          />
        );

      case State.GUIDE:
        return (
          <Guide
            idDocumentCase={
              state.context.idDocumentCase ??
              IdDocumentCase.AcceptedPassportOrIdCard
            }
            isAllowedToSkipOnfidoSelfie={
              moduleData.viewer.companyUser.isAllowedToSkipOnfidoSelfie
            }
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.BIOMETRIC_CONSENT:
        return (
          <BiometricConsent
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );

      case State.ONFIDO_NATIVE_IDENTITY_ERROR: {
        const error =
          state.event.type === 'done.invoke.completeNativeIdCheck' &&
          state.event.data.status === 'error'
            ? state.event.data.error
            : null;

        return <NativeIdCheckError onfidoErrorCode={error} onPrev={onPrev} />;
      }

      case State.IDENTITY_RECAP: {
        const identityValues = getInitialFormValues(moduleData.viewer.profile);

        if (!identitySchema.safeParse(identityValues).success) {
          logger.error(
            `${State.IDENTITY_RECAP} state reached but identity values are not valid`,
          );
        }

        return (
          <Recap
            hasIdentityBeenVerified={companyUsers.some(
              (user) => user.reviewStatus === CompanyUserReviewStatus.Validated,
            )}
            identityStatus={identityStatus}
            identityValues={identityValues}
            onNext={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.FOURTHLINE_ID_CHECK:
        if (!state.context.idCheckContext) {
          throw new Error(
            `idCheckToken is required in ${State.FOURTHLINE_ID_CHECK}`,
          );
        }

        return (
          /**
           * Make sure these props are stable, in particular the fourthline handlers
           * that are registered on event listeners.
           */
          <FourthlineIdCheck
            companyProfileId={moduleData.viewer.companyUser.companyProfileId}
            fourthlineToken={state.context.idCheckContext.token}
            onPrev={onPrev}
            onRestartIdCheck={handleFourthlineRestartIdCheck}
            onVerificationCompleted={handleFourthlineVerificationCompleted}
          />
        );

      case State.ONFIDO_IDENTITY_CHECK: {
        if (!state.context?.idCheckContext) {
          throw new Error(
            `idCheckContext is required in state ${stringifiedState}`,
          );
        }

        return (
          <OnfidoIdCheck
            isAllowedToSkipOnfidoSelfie={
              moduleData.viewer.companyUser.isAllowedToSkipOnfidoSelfie
            }
            onfidoToken={state.context.idCheckContext.token}
            onPrev={onPrev}
            onSuccess={(documents) =>
              send(model.events.ONFIDO_SUCCESS(documents))
            }
          />
        );
      }

      case State.SUCCESS: {
        return (
          <IdentitySuccess
            onContinue={() => send(model.events.NEXT())}
            onPrev={onPrev}
          />
        );
      }

      case State.INIT:
      case State.DONE:
      case State.START_IDENTITY_VERIFICATION:
      case State.FOURTHLINE_NATIVE_WORKFLOW:
      case State.FINISH_IDENTITY_VERIFICATION:
      case State.POST_FOURTHLINE_ID_CHECK_REDIRECTION:
      case State.ABORT:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },
  {
    devTools: true,
    machineConfig,
  },
);

export default Identity;
